import { Link } from "react-router-dom";
import { Mail, Handshake, ShieldQuestion, Sparkles } from "lucide-react";
import { BETA_EMAIL, BETA_MAILTO } from "@/components/Layout";

const REASONS = [
  {
    icon: Sparkles,
    title: "Request early access",
    body: "Want into the beta? Tell us a little about yourself and how you stay in touch today.",
  },
  {
    icon: Handshake,
    title: "Partnerships",
    body: "Building something complementary, or curious about working together? Say hello.",
  },
  {
    icon: ShieldQuestion,
    title: "Privacy & security",
    body: "Questions about how we handle your data, or reporting a security concern? We're listening.",
  },
];

export default function Contact() {
  return (
    <div data-testid="contact-page" className="bg-[#f8fafc]">
      <section className="border-b border-slate-200 bg-white">
        <div className="max-w-3xl mx-auto px-5 sm:px-8 py-16 sm:py-20">
          <Link
            to="/"
            className="text-sm font-medium text-blue-700 hover:text-blue-800 transition-colors"
            data-testid="contact-back-home"
          >
            ← Back to home
          </Link>
          <h1 className="mt-6 text-4xl sm:text-5xl font-bold tracking-tight text-slate-900">
            Get in touch
          </h1>
          <p className="mt-4 text-base sm:text-lg text-slate-600 leading-relaxed">
            We're a small team building in the open during beta. The fastest way to reach
            us is good old-fashioned email — and yes, a real human reads it.
          </p>

          <div className="mt-8 rounded-2xl border border-slate-200 bg-[#f8fafc] p-7 sm:p-8">
            <p className="text-sm font-medium text-slate-500">Email us</p>
            <a
              href={`mailto:${BETA_EMAIL}`}
              data-testid="contact-email-link"
              className="mt-1 inline-flex items-center gap-2 text-2xl sm:text-3xl font-bold tracking-tight text-blue-700 hover:text-blue-800 transition-colors break-all"
            >
              {BETA_EMAIL}
            </a>
            <div className="mt-6">
              <a
                href={BETA_MAILTO}
                data-testid="contact-beta-cta"
                className="inline-flex items-center gap-2 bg-blue-700 text-white rounded-lg px-6 py-3.5 text-base font-semibold hover:bg-blue-800 transition-colors shadow-sm active:scale-95"
              >
                <Mail className="h-5 w-5" />
                Request beta access
              </a>
            </div>
            <p className="mt-4 text-sm text-slate-500">
              Interested in early access? Email the address above and we'll get back to you.
            </p>
          </div>
        </div>
      </section>

      <div className="max-w-3xl mx-auto px-5 sm:px-8 py-14 sm:py-16">
        <h2 className="text-xl sm:text-2xl font-semibold tracking-tight text-slate-900">
          What you can reach us about
        </h2>
        <div className="mt-6 grid gap-5 sm:grid-cols-3">
          {REASONS.map((r) => {
            const Icon = r.icon;
            return (
              <div
                key={r.title}
                className="bg-white rounded-2xl border border-slate-200 p-7"
                data-testid={`contact-reason-${r.title.toLowerCase().replace(/[^a-z]+/g, "-").replace(/(^-|-$)/g, "")}`}
              >
                <div className="h-11 w-11 rounded-xl bg-blue-50 text-blue-700 flex items-center justify-center">
                  <Icon className="h-5 w-5" />
                </div>
                <h3 className="mt-5 text-base font-semibold text-slate-900">
                  {r.title}
                </h3>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed">{r.body}</p>
              </div>
            );
          })}
        </div>

        <p className="mt-10 text-sm text-slate-500">
          Prefer to read first? See our{" "}
          <Link to="/privacy" className="text-blue-700 font-medium hover:underline">
            Privacy Policy
          </Link>
          ,{" "}
          <Link to="/security" className="text-blue-700 font-medium hover:underline">
            Security
          </Link>{" "}
          practices, and{" "}
          <Link to="/terms" className="text-blue-700 font-medium hover:underline">
            Terms
          </Link>
          .
        </p>
      </div>
    </div>
  );
}
